import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Cpu } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";

const PILL = {
  healthy: "bg-green-100 text-green-700",
  degraded: "bg-amber-100 text-amber-700",
  down: "bg-red-100 text-red-700",
  unreachable: "bg-red-100 text-red-700",
};

export const TensornexAdminCard = () => {
  const [key, setKey] = useState("");
  const [status, setStatus] = useState(null);
  const [engine, setEngine] = useState("tensornex_first");
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState("");

  const load = async () => {
    try {
      const { data: s } = await api.get("/admin/settings");
      setKey((s.tensornex || {}).api_key || "");
      const { data } = await api.get("/admin/tensornex/status");
      setStatus(data);
      setEngine(data.stt_engine || "tensornex_first");
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (vals) => {
    try {
      await api.put("/admin/settings", { section: "tensornex", values: vals });
      toast.success("Tensornex settings saved");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const runTest = async () => {
    setBusy(true); setTestResult("");
    try {
      const { data } = await api.post("/admin/tensornex/test");
      setTestResult(`✓ Docling parsed the sample: "${(data.snippet || "").slice(0, 120)}…"`);
      toast.success("Tensornex test passed");
    } catch (e) {
      setTestResult(`✗ ${errDetail(e)}`);
      toast.error(errDetail(e));
    }
    setBusy(false);
  };

  const health = status?.health || "unreachable";
  return (
    <div data-testid="tensornex-admin-card" className="bg-white border border-gray-200 rounded-md p-5 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Cpu size={20} className="text-indigo-600" />
        <h3 className="font-heading font-bold text-gray-900">Tensornex</h3>
        <span data-testid="tnx-health-pill" className={`text-[10px] font-bold rounded-full px-2 py-0.5 uppercase ${PILL[health] || "bg-gray-100 text-gray-500"}`}>{health}</span>
      </div>
      <p className="text-xs text-gray-500 mb-4">Document AI (Docling) for reading email attachments + Transcribe for WhatsApp voice notes. Needs the <b>docling</b> and <b>minio</b> services subscribed on tensornex.cloud.</p>
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <input data-testid="tnx-key-input" value={key} onChange={(e) => setKey(e.target.value)}
          placeholder="gs_live_…" className="border border-gray-300 rounded-md px-3 py-2 text-sm w-80 font-mono" />
        <button data-testid="tnx-save-btn" onClick={() => save({ api_key: key })}
          className="text-sm font-semibold bg-gray-900 text-white rounded-md px-3 py-2">Save</button>
        <button data-testid="tnx-test-btn" onClick={runTest} disabled={busy || !status?.configured}
          className="text-sm font-semibold border border-indigo-300 text-indigo-700 hover:bg-indigo-50 rounded-md px-3 py-2 disabled:opacity-50 transition-colors">
          {busy ? "Testing…" : "Test"}
        </button>
      </div>
      {testResult && <p data-testid="tnx-test-result" className={`text-xs mb-3 ${testResult.startsWith("✓") ? "text-green-700" : "text-red-600"}`}>{testResult}</p>}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-semibold text-gray-500 uppercase">WhatsApp voice engine</span>
        <select data-testid="tnx-stt-engine-select" value={engine}
          onChange={(e) => { setEngine(e.target.value); save({ stt_engine: e.target.value }); }}
          className="border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white">
          <option value="tensornex_first">Tensornex first, Whisper fallback (recommended)</option>
          <option value="whisper_only">Whisper only</option>
          <option value="tensornex_only">Tensornex only (no fallback)</option>
        </select>
      </div>
      {status?.usage && (
        <p data-testid="tnx-usage-line" className="text-[11px] text-gray-400">
          Services: {(status.usage.enabled_services || []).join(", ") || "—"} · Requests today: {status.usage.requests_today?.used ?? "?"}/{status.usage.requests_today?.limit ?? "?"} · Jobs: {status.usage.active_jobs?.current ?? 0}/{status.usage.active_jobs?.limit ?? 10}
        </p>
      )}
      {!status?.configured && <p className="text-[11px] text-amber-600">No API key saved yet — attachments won't be parsed and WhatsApp voice will use Whisper.</p>}
    </div>
  );
};
